import {useMutation, useQueryClient} from "@tanstack/react-query";

import {errorHandler} from "@/src/modules/core/lib";
import {captureParkingSubmitError} from "@/src/modules/core/lib/glitchtip/parking-capture";
import type {Shift} from "@/src/modules/shifts/types";

import {mergeShiftCompanyVehiclesAfterStart} from "../lib/shift-company-vehicles";
import {
  companyVehicleCustodyFromCheckNested,
  parseVehicleConditionCheckCreateResponse,
} from "../lib/vehicle-condition-check-create-response";
import {companyVehiclesService} from "../services";
import type {VehicleConditionCheckPayload} from "../types";

/** Variables for the mutation (API body + client-only flags). */
export type CreateVehicleConditionCheckVariables = {
  payload: VehicleConditionCheckPayload;
  /**
   * When the next step is `POST shift/end` (token invalidated), skip eager refetches so the POST
   * is not followed by GET company-vehicles / vehicle-condition-checks / active-shift.
   */
  skipEagerInvalidation?: boolean;
};

export function useCreateVehicleConditionCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({payload}: CreateVehicleConditionCheckVariables) =>
      companyVehiclesService.createConditionCheck(payload),
    onSuccess: (data, variables) => {
      if (variables.skipEagerInvalidation) {
        return;
      }

      /**
       * Apply start-check custody to `active-shift` **before** any refetch so Home can close the
       * vehicle modal immediately; the following GETs may lag or fail.
       */
      if (variables.payload.phase === "start") {
        const shiftId = variables.payload.shift_id;
        const parsed = parseVehicleConditionCheckCreateResponse(data);
        const fromPost =
          parsed && parsed.shift_id === shiftId
            ? companyVehicleCustodyFromCheckNested(parsed.company_vehicle)
            : null;
        const vehicleId = variables.payload.company_vehicle_id;
        const custody =
          fromPost ??
          (vehicleId != null && Number.isFinite(Number(vehicleId))
            ? {id: Number(vehicleId), name: `Vehicle #${vehicleId}`}
            : null);
        if (custody != null) {
          queryClient.setQueryData<{shift: Shift | null}>(["active-shift"], (old) => {
            const shift = old?.shift;
            if (!shift || shift.shift_id !== shiftId) return old ?? {shift: null};
            return {
              shift: {
                ...shift,
                vehicle_condition_cycle_status: "started",
                company_vehicle: mergeShiftCompanyVehiclesAfterStart(shift, custody),
              } as Shift,
            };
          });
        }
      }

      void queryClient.invalidateQueries({queryKey: ["company-vehicles"]});
      void queryClient.invalidateQueries({queryKey: ["vehicleconditionchecks"]});
      void queryClient.invalidateQueries({queryKey: ["vehicleconditionchecks", "by-shift"]});
      void queryClient.invalidateQueries({queryKey: ["active-shift"]});
      void queryClient.refetchQueries({queryKey: ["active-shift"]});
    },
    onError: (error, variables) => {
      captureParkingSubmitError(error, {
        feature: "companyvehicles",
        operation:
          variables.payload.phase === "start"
            ? "patrol_vehicle_condition_start"
            : "patrol_vehicle_condition_end",
      });
      errorHandler(error);
    },
  });
}
